import {inject, Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';
import {ConnectApiService} from './connect-api.service';
import {ConnectWorkflowNode} from './connect-workflows.service';

export type ConnectWorkflowNodeCategory = 'Triggers' | 'Logic' | 'Integrations' | 'Utils';
export type ConnectNodeOperationEffect = 'read' | 'write' | 'delete';
export type ConnectNodeJsonScalar = string | number | boolean | null;
export type ConnectNodeJsonType = 'string' | 'number' | 'integer' | 'boolean' | 'null' | 'array' | 'object';

export const CONNECT_GUIDED_REQUEST_BINDINGS = [
  {value: '$.manual.subject', label: 'Request subject'},
  {value: '$.manual.details', label: 'Request details'},
  {value: '$.manual.priority', label: 'Request priority'},
] as const;

export const CONNECT_GUIDED_CURRENT_ITEM_BINDINGS = [
  {value: '{{item.subject}}', label: 'Current item subject'},
  {value: '{{item.details}}', label: 'Current item details'},
  {value: '{{item.priority}}', label: 'Current item priority'},
] as const;

export type ConnectGuidedBindingToken =
  | typeof CONNECT_GUIDED_REQUEST_BINDINGS[number]['value']
  | typeof CONNECT_GUIDED_CURRENT_ITEM_BINDINGS[number]['value']
  | ConnectGuidedPriorStepBindingToken;

export type ConnectGuidedPriorStepBindingToken = `$.${string}.output.${string}`;

export type ConnectGuidedPriorStepBinding = {
  value: ConnectGuidedPriorStepBindingToken;
  label: string;
  producerNodeId: string;
  operationRef: string;
  outputPath: string;
  shape: ConnectNodeFieldShape;
};

export type ConnectNodeFieldShape = {
  type?: ConnectNodeJsonType | ConnectNodeJsonType[];
  enum?: ConnectNodeJsonScalar[];
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  minItems?: number;
  maxItems?: number;
  items?: ConnectNodeFieldShape;
  properties?: Record<string, ConnectNodeFieldShape>;
  required?: string[];
  additionalProperties?: false | ConnectNodeFieldShape;
  oneOf?: ConnectNodeFieldShape[];
};

export type ConnectNodePresentationField = {
  label: string;
  help?: string;
  shape: ConnectNodeFieldShape;
};

export type ConnectNodePresentationOperation = {
  action: string;
  label: string;
  help?: string;
  effect: ConnectNodeOperationEffect;
  fields: string[];
  requires_all?: string[];
  requires_any?: string[][];
};

export type ConnectNodePresentation = {
  fields: Record<string, ConnectNodePresentationField>;
  operations: ConnectNodePresentationOperation[];
};

export type ConnectNodeOperationOutput = Record<string, ConnectNodeFieldShape>;
export type ConnectNodeOutput = Record<string, ConnectNodeOperationOutput>;

export type ConnectWorkflowNodePaletteItem = {
  /**
   * Unique palette identifier. For core nodes it matches `workflowType`.
   * For provider shortcuts it is a stable catalog id (e.g. `hubspot.companies`).
   */
  paletteId: string;
  /**
   * The node `type` persisted in the workflow document (must be supported by the Connect backend schema).
   */
  workflowType: string;
  label: string;
  description?: string;
  category: ConnectWorkflowNodeCategory;
  providerId?: string;
  /**
   * Optional action list for connector-backed nodes (ref suffixes).
   */
  actions?: string[];
  defaults?: Partial<ConnectWorkflowNode>;
  /**
   * Optional, value-safe form metadata. Invalid metadata is ignored without
   * removing the established advanced-palette node or its action authority.
   */
  presentation?: ConnectNodePresentation;
  /** Optional admitted, value-safe scalar output paths keyed by exact action. */
  output?: ConnectNodeOutput;
};

export type ConnectNodeCatalog = {
  version: 'v1';
  nodes: ConnectWorkflowNodePaletteItem[];
};

export type ConnectGuidedConnectorProjection = {
  kind: 'connector-operation';
  operationRef: string;
  connectionSlot: string;
  connectionProviderRef: string;
  input: Record<string, unknown>;
};

const CATEGORIES = new Set<ConnectWorkflowNodeCategory>(['Triggers', 'Logic', 'Integrations', 'Utils']);
const EFFECTS = new Set<ConnectNodeOperationEffect>(['read', 'write', 'delete']);
const JSON_TYPES = new Set<ConnectNodeJsonType>([
  'string', 'number', 'integer', 'boolean', 'null', 'array', 'object',
]);
const FIELD_PATH = /^[A-Za-z_][A-Za-z0-9_-]*(\.[A-Za-z_][A-Za-z0-9_-]*)*$/;
const RESERVED_OBJECT_KEYS = new Set(['__proto__', 'prototype', 'constructor']);
const SHAPE_KEYS = new Set([
  'type', 'enum', 'minimum', 'maximum', 'minLength', 'maxLength', 'minItems', 'maxItems', 'items',
  'properties', 'required', 'additionalProperties', 'oneOf',
]);
const MAX_FIELDS = 100;
const MAX_OPERATIONS = 100;
const MAX_FIELD_PATH_LENGTH = 160;
const MAX_SHAPE_DEPTH = 6;
const MAX_SHAPE_NODES = 128;
const MAX_SHAPE_PROPERTIES = 32;
const MAX_ONE_OF = 4;
const MAX_ENUM_VALUES = 50;
const MAX_ENUM_STRING_LENGTH = 256;

type ShapeBudget = {nodes: number};
type FieldPathTree = {terminal: boolean; children: Map<string, FieldPathTree>};
export type ConnectGuidedPriorStepBindingValidator = (
  binding: ConnectGuidedPriorStepBindingToken,
  targetShape: ConnectNodeFieldShape,
) => boolean;

type ConnectNodeOperationInputOptions = {
  allowGuidedBindings?: boolean;
  validatePriorStepBinding?: ConnectGuidedPriorStepBindingValidator;
};

const GUIDED_BINDING_TOKENS = new Set<string>([
  ...CONNECT_GUIDED_REQUEST_BINDINGS.map(binding => binding.value),
  ...CONNECT_GUIDED_CURRENT_ITEM_BINDINGS.map(binding => binding.value),
]);
const CURRENT_ITEM_BINDING_TOKENS = new Set<string>(
  CONNECT_GUIDED_CURRENT_ITEM_BINDINGS.map(binding => binding.value),
);

export function readConnectNodeCatalog(value: unknown): ConnectNodeCatalog {
  if (!isRecord(value) || value['version'] !== 'v1' || !Array.isArray(value['nodes'])) {
    throw new Error('Connect node catalog is unavailable.');
  }

  const nodes = value['nodes'].map(readPaletteItem);
  return {version: 'v1', nodes};
}

export function readConnectNodePresentation(
  value: unknown,
  admittedActions: readonly string[],
): ConnectNodePresentation | null {
  if (!isExactRecord(value, ['fields', 'operations']) ||
      !isRecord(value['fields']) ||
      Object.keys(value['fields']).length > MAX_FIELDS ||
      !Array.isArray(value['operations']) ||
      value['operations'].length < 1 ||
      value['operations'].length > MAX_OPERATIONS) {
    return null;
  }

  const actionAuthority = new Map<string, string>();
  for (const action of admittedActions) {
    if (!isBoundedText(action, 120)) return null;
    const key = action.toLowerCase();
    if (actionAuthority.has(key)) return null;
    actionAuthority.set(key, action);
  }

  const fields: Record<string, ConnectNodePresentationField> = {};
  for (const [fieldPath, candidate] of Object.entries(value['fields'])) {
    if (!isFieldPath(fieldPath) || !isExactRecord(candidate, ['label', 'help', 'shape']) ||
        !isBoundedText(candidate['label'], 120) ||
        candidate['help'] !== undefined && !isBoundedText(candidate['help'], 500)) {
      return null;
    }
    const shape = readFieldShape(candidate['shape'], 0, {nodes: 0});
    if (!shape) return null;
    fields[fieldPath] = {
      label: candidate['label'].trim(),
      ...(candidate['help'] === undefined ? {} : {help: candidate['help'].trim()}),
      shape,
    };
  }

  const operations: ConnectNodePresentationOperation[] = [];
  const seenActions = new Set<string>();
  for (const candidate of value['operations']) {
    if (!isExactRecord(candidate, [
      'action', 'label', 'help', 'effect', 'fields', 'requires_all', 'requires_any',
    ]) || !isBoundedText(candidate['action'], 120) || !isBoundedText(candidate['label'], 120) ||
        candidate['help'] !== undefined && !isBoundedText(candidate['help'], 500) ||
        typeof candidate['effect'] !== 'string' ||
        !EFFECTS.has(candidate['effect'] as ConnectNodeOperationEffect)) {
      return null;
    }

    const authorityAction = actionAuthority.get(candidate['action'].trim().toLowerCase());
    const fieldNames = readUniqueFieldPaths(candidate['fields'], true);
    const requiresAll = candidate['requires_all'] === undefined
      ? []
      : readUniqueFieldPaths(candidate['requires_all'], false);
    const requiresAny = candidate['requires_any'] === undefined
      ? []
      : readFieldPathGroups(candidate['requires_any']);
    const actionKey = candidate['action'].trim().toLowerCase();
    if (!authorityAction || seenActions.has(actionKey) || !fieldNames || !requiresAll || !requiresAny ||
        fieldNames.some(field => fields[field] === undefined) ||
        requiresAll.some(field => !fieldNames.includes(field)) ||
        requiresAny.some(group => group.some(field => !fieldNames.includes(field))) ||
        hasAmbiguousFieldPaths(fieldNames)) {
      return null;
    }
    seenActions.add(actionKey);
    operations.push({
      action: authorityAction,
      label: candidate['label'].trim(),
      ...(candidate['help'] === undefined ? {} : {help: candidate['help'].trim()}),
      effect: candidate['effect'] as ConnectNodeOperationEffect,
      fields: fieldNames,
      ...(requiresAll.length === 0 ? {} : {requires_all: requiresAll}),
      ...(requiresAny.length === 0 ? {} : {requires_any: requiresAny}),
    });
  }

  const referencedFields = new Set(operations.flatMap(operation => operation.fields));
  const projectedFields = Object.fromEntries(
    Object.entries(fields).filter(([field]) => referencedFields.has(field)),
  );
  return {fields: projectedFields, operations};
}

/**
 * Validates and clones one novice connector input using only the selected
 * operation's admitted presentation contract. Optional blank values are
 * omitted; unknown fields and unsupported values fail closed.
 */
export function readConnectNodeOperationInput(
  presentation: ConnectNodePresentation,
  action: string,
  value: unknown,
  options: ConnectNodeOperationInputOptions = {},
): Record<string, unknown> | null {
  if (!isRecord(value)) return null;
  const operations = presentation.operations.filter(
    operation => operation.action.toLowerCase() === action.trim().toLowerCase(),
  );
  if (operations.length !== 1) return null;
  const operation = operations[0];
  const pathTree = buildFieldPathTree(operation.fields);
  if (!pathTree || !containsOnlyDeclaredPaths(value, pathTree)) return null;

  const normalized: Record<string, unknown> = {};
  const present = new Set<string>();
  for (const fieldPath of operation.fields) {
    const found = readPath(value, fieldPath);
    if (!found.present || isBlankOptionalValue(found.value)) continue;
    const field = presentation.fields[fieldPath];
    const allowFieldBinding = options.allowGuidedBindings === true &&
      !(operation.effect !== 'read' && field?.shape.enum?.length === 1);
    const parsed = normalizeShapeValue(field?.shape, found.value, {
      allowGuidedBindings: allowFieldBinding,
      validatePriorStepBinding: options.validatePriorStepBinding,
    });
    if (!parsed.valid) return null;
    setPath(normalized, fieldPath, parsed.value);
    present.add(fieldPath);
  }

  if ((operation.requires_all ?? []).some(field => !present.has(field)) ||
      (operation.requires_any ?? []).some(group => !group.some(field => present.has(field)))) {
    return null;
  }
  return normalized;
}

export function projectConnectGuidedConnectorOperation(
  node: ConnectWorkflowNode,
  providerRef: string,
  catalog: readonly ConnectWorkflowNodePaletteItem[],
): ConnectGuidedConnectorProjection | null {
  if (node.type !== 'api.connector' || typeof node.ref !== 'string' ||
      typeof node['connection'] !== 'string' || !isRecord(node['input'])) {
    return null;
  }
  const matches = catalog.flatMap(candidate => (candidate.presentation?.operations ?? [])
    .filter(operation => candidate.workflowType === node.type &&
      `${candidate.paletteId}.${operation.action}` === node.ref)
    .map(operation => ({candidate, operation})));
  if (matches.length !== 1 || matches[0].candidate.providerId === undefined) return null;
  const normalizedInput = readConnectNodeOperationInput(
    matches[0].candidate.presentation!,
    matches[0].operation.action,
    node['input'],
    {
      allowGuidedBindings: true,
      validatePriorStepBinding: binding => parseConnectGuidedPriorStepBindingToken(binding) !== null,
    },
  );
  if (!normalizedInput) return null;
  return {
    kind: 'connector-operation',
    operationRef: node.ref,
    connectionSlot: node['connection'],
    connectionProviderRef: providerRef,
    input: normalizedInput,
  };
}

export function isConnectGuidedBindingToken(value: unknown): value is ConnectGuidedBindingToken {
  return typeof value === 'string' && (
    GUIDED_BINDING_TOKENS.has(value) || parseConnectGuidedPriorStepBindingToken(value) !== null
  );
}

export function isConnectGuidedCurrentItemBindingToken(value: unknown): value is ConnectGuidedBindingToken {
  return typeof value === 'string' && CURRENT_ITEM_BINDING_TOKENS.has(value);
}

export function isConnectGuidedRuntimeLikeString(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  const normalized = trimmed.toLowerCase();
  return trimmed.startsWith('$')
    || normalized === 'ctx'
    || normalized.startsWith('ctx.')
    || value.includes('{{')
    || value.includes('}}');
}

export function parseConnectGuidedPriorStepBindingToken(
  value: unknown,
): {producerNodeId: string; outputPath: string} | null {
  if (typeof value !== 'string' || value.length > 360) return null;
  const match = /^\$\.([A-Za-z0-9_-]{1,160})\.output\.([A-Za-z_][A-Za-z0-9_-]*(?:\.[A-Za-z_][A-Za-z0-9_-]*)*)$/
    .exec(value);
  if (!match || !isFieldPath(match[2])) return null;
  return {producerNodeId: match[1], outputPath: match[2]};
}

export function resolveConnectNodeOperation(
  catalog: readonly ConnectWorkflowNodePaletteItem[],
  operationRef: string,
): {
  node: ConnectWorkflowNodePaletteItem;
  operation: ConnectNodePresentationOperation;
  output: ConnectNodeOperationOutput;
} | null {
  const matches = catalog.flatMap(node => (node.presentation?.operations ?? [])
    .filter(operation => `${node.paletteId}.${operation.action}` === operationRef)
    .map(operation => ({
      node,
      operation,
      output: node.output?.[operation.action] ?? {},
    })));
  return matches.length === 1 ? matches[0] : null;
}

export function isConnectNodeOutputCompatible(
  outputShape: ConnectNodeFieldShape,
  targetShape: ConnectNodeFieldShape,
): boolean {
  if (outputShape.oneOf) {
    return outputShape.oneOf.length > 0
      && outputShape.oneOf.every(candidate => isConnectNodeOutputCompatible(candidate, targetShape));
  }
  if (targetShape.oneOf) {
    return targetShape.oneOf.some(candidate => isConnectNodeOutputCompatible(outputShape, candidate));
  }
  const outputTypes = shapeTypes(outputShape);
  const targetTypes = shapeTypes(targetShape);
  if (outputTypes.length === 0 || targetTypes.length === 0 ||
      outputTypes.some(type => type === 'array' || type === 'object') ||
      outputShape.enum === undefined &&
      outputTypes.some(type => !targetTypes.some(target => outputTypeAssignableToTarget(type, target)))) {
    return false;
  }
  if (outputShape.enum) {
    return outputShape.enum.every(value => shapeAcceptsScalar(targetShape, value));
  }
  if (targetShape.enum) return false;
  if (targetShape.minLength !== undefined &&
      (outputShape.minLength === undefined || outputShape.minLength < targetShape.minLength)) return false;
  if (targetShape.maxLength !== undefined &&
      (outputShape.maxLength === undefined || outputShape.maxLength > targetShape.maxLength)) return false;
  if (targetShape.minimum !== undefined &&
      (outputShape.minimum === undefined || outputShape.minimum < targetShape.minimum)) return false;
  if (targetShape.maximum !== undefined &&
      (outputShape.maximum === undefined || outputShape.maximum > targetShape.maximum)) return false;
  return true;
}

@Injectable({
  providedIn: 'root',
})
export class ConnectNodesService {
  private readonly api = inject(ConnectApiService);

  fetchCatalog(): Observable<ConnectNodeCatalog> {
    return this.api.get<unknown>(`/api/v1/nodes`).pipe(map(readConnectNodeCatalog));
  }
}

function readPaletteItem(value: unknown): ConnectWorkflowNodePaletteItem {
  if (!isRecord(value) || !isBoundedText(value['paletteId'], 160) ||
      !isBoundedText(value['workflowType'], 160) || !isBoundedText(value['label'], 160) ||
      typeof value['category'] !== 'string' ||
      !CATEGORIES.has(value['category'] as ConnectWorkflowNodeCategory) ||
      value['description'] !== undefined && !isBoundedText(value['description'], 500) ||
      value['providerId'] !== undefined && !isBoundedText(value['providerId'], 160)) {
    throw new Error('Connect node catalog contains an invalid node.');
  }

  let actions: string[] | undefined;
  if (value['actions'] !== undefined) {
    actions = readUniqueTextList(value['actions'], 120);
    if (!actions) throw new Error('Connect node catalog contains invalid actions.');
  }
  let defaults: Partial<ConnectWorkflowNode> | undefined;
  if (value['defaults'] !== undefined) {
    if (!isRecord(value['defaults'])) throw new Error('Connect node catalog contains invalid defaults.');
    defaults = cloneJson(value['defaults']) as Partial<ConnectWorkflowNode>;
  }

  const presentation = actions
    ? readConnectNodePresentation(value['presentation'], actions)
    : null;
  const output = actions ? readConnectNodeOutput(value['output'], actions) : null;
  return {
    paletteId: value['paletteId'].trim(),
    workflowType: value['workflowType'].trim(),
    label: value['label'].trim(),
    ...(value['description'] === undefined ? {} : {description: value['description'].trim()}),
    category: value['category'] as ConnectWorkflowNodeCategory,
    ...(value['providerId'] === undefined ? {} : {providerId: value['providerId'].trim()}),
    ...(actions === undefined ? {} : {actions}),
    ...(defaults === undefined ? {} : {defaults}),
    ...(presentation === null ? {} : {presentation}),
    ...(output === null ? {} : {output}),
  };
}

function readConnectNodeOutput(value: unknown, admittedActions: readonly string[]): ConnectNodeOutput | null {
  if (value === undefined) return null;
  if (!isRecord(value) || Object.keys(value).length > MAX_OPERATIONS) return null;
  const actions = new Map(admittedActions.map(action => [action.toLowerCase(), action]));
  const result: ConnectNodeOutput = {};
  const seen = new Set<string>();
  for (const [candidateAction, candidateFields] of Object.entries(value)) {
    const action = actions.get(candidateAction.toLowerCase());
    if (!action || seen.has(action.toLowerCase()) || !isRecord(candidateFields) ||
        Object.keys(candidateFields).length < 1 || Object.keys(candidateFields).length > MAX_FIELDS) return null;
    const fields: ConnectNodeOperationOutput = {};
    for (const [path, candidateShape] of Object.entries(candidateFields)) {
      if (!isFieldPath(path)) return null;
      const shape = readFieldShape(candidateShape, 0, {nodes: 0});
      if (!shape || !isScalarShape(shape)) return null;
      fields[path] = shape;
    }
    seen.add(action.toLowerCase());
    result[action] = fields;
  }
  return Object.keys(result).length > 0 ? result : null;
}

function readFieldShape(value: unknown, depth: number, budget: ShapeBudget): ConnectNodeFieldShape | null {
  if (!isRecord(value) || depth > MAX_SHAPE_DEPTH || ++budget.nodes > MAX_SHAPE_NODES ||
      Object.keys(value).some(key => !SHAPE_KEYS.has(key))) {
    return null;
  }
  if (value['oneOf'] !== undefined) {
    if (Object.keys(value).length !== 1 || !Array.isArray(value['oneOf']) ||
        value['oneOf'].length < 2 || value['oneOf'].length > MAX_ONE_OF) return null;
    const oneOf = value['oneOf'].map(candidate => readFieldShape(candidate, depth + 1, budget));
    if (oneOf.some(candidate => candidate === null)) return null;
    const usedTypes: ConnectNodeJsonType[] = [];
    for (const candidate of oneOf as ConnectNodeFieldShape[]) {
      const types = shapeTypes(candidate);
      if (types.length === 0 || types.some(type => usedTypes.some(used => typesOverlap(type, used)))) return null;
      usedTypes.push(...types);
    }
    return {oneOf: oneOf as ConnectNodeFieldShape[]};
  }

  const types = readJsonTypes(value['type']);
  if (!types || types.length === 0 ||
      (types.includes('array') || types.includes('object')) && types.length !== 1) return null;
  const result: ConnectNodeFieldShape = {type: types.length === 1 ? types[0] : types};
  const enumValues = readEnum(value['enum'], types);
  if (value['enum'] !== undefined && !enumValues) return null;
  if (enumValues) result.enum = enumValues;

  const minimum = readFiniteNumber(value, 'minimum');
  const maximum = readFiniteNumber(value, 'maximum');
  const supportsNumber = types.includes('number') || types.includes('integer');
  if (minimum.invalid || maximum.invalid || !supportsNumber && (minimum.present || maximum.present) ||
      minimum.present && maximum.present && minimum.value! > maximum.value!) return null;
  if (minimum.present) result.minimum = minimum.value;
  if (maximum.present) result.maximum = maximum.value;

  for (const [minimumKey, maximumKey, requiredType] of [
    ['minLength', 'maxLength', 'string'],
    ['minItems', 'maxItems', 'array'],
  ] as const) {
    const lower = readNonNegativeInteger(value, minimumKey);
    const upper = readNonNegativeInteger(value, maximumKey);
    if (lower.invalid || upper.invalid || !types.includes(requiredType) && (lower.present || upper.present) ||
        lower.present && upper.present && lower.value! > upper.value!) return null;
    if (lower.present) result[minimumKey] = lower.value;
    if (upper.present) result[maximumKey] = upper.value;
  }

  if (types.includes('array')) {
    const items = readFieldShape(value['items'], depth + 1, budget);
    if (!items) return null;
    result.items = items;
  } else if (value['items'] !== undefined || value['minItems'] !== undefined || value['maxItems'] !== undefined) {
    return null;
  }

  if (types.includes('object')) {
    const objectShape = readObjectShape(value, depth, budget);
    if (!objectShape) return null;
    Object.assign(result, objectShape);
  } else if (value['properties'] !== undefined || value['required'] !== undefined ||
             value['additionalProperties'] !== undefined) {
    return null;
  }
  return result;
}

function readObjectShape(
  value: Record<string, unknown>,
  depth: number,
  budget: ShapeBudget,
): Pick<ConnectNodeFieldShape, 'properties' | 'required' | 'additionalProperties'> | null {
  let properties: Record<string, ConnectNodeFieldShape> | undefined;
  if (value['properties'] !== undefined) {
    if (!isRecord(value['properties']) || Object.keys(value['properties']).length > MAX_SHAPE_PROPERTIES) return null;
    properties = {};
    for (const [name, candidate] of Object.entries(value['properties'])) {
      if (!name.trim() || !isSafeObjectKey(name)) return null;
      const shape = readFieldShape(candidate, depth + 1, budget);
      if (!shape) return null;
      properties[name] = shape;
    }
  }
  let required: string[] | undefined;
  if (value['required'] !== undefined) {
    required = readUniqueTextList(value['required'], MAX_FIELD_PATH_LENGTH);
    if (!required || !properties || required.some(name => properties?.[name] === undefined)) return null;
  }
  if (value['additionalProperties'] === false) {
    return {properties, required, additionalProperties: false};
  }
  const additionalProperties = readFieldShape(value['additionalProperties'], depth + 1, budget);
  if (!additionalProperties || shapeTypes(additionalProperties).some(type => type === 'array' || type === 'object')) {
    return null;
  }
  return {properties, required, additionalProperties};
}

function normalizeShapeValue(
  shape: ConnectNodeFieldShape | undefined,
  value: unknown,
  options: {
    allowGuidedBindings: boolean;
    validatePriorStepBinding?: ConnectGuidedPriorStepBindingValidator;
  },
): {valid: true; value: unknown} | {valid: false} {
  if (!shape) return {valid: false};
  if (shape.oneOf) {
    const matches = shape.oneOf
      .map(candidate => normalizeShapeValue(candidate, value, options))
      .filter(result => result.valid);
    return matches.length === 1 ? matches[0] : {valid: false};
  }
  const types = shapeTypes(shape);
  if (isConnectGuidedRuntimeLikeString(value)) {
    const prior = parseConnectGuidedPriorStepBindingToken(value);
    const allowed = prior
      ? options.validatePriorStepBinding?.(value as ConnectGuidedPriorStepBindingToken, shape) === true
      : GUIDED_BINDING_TOKENS.has(value as string);
    return options.allowGuidedBindings && allowed && types.includes('string')
      ? {valid: true, value}
      : {valid: false};
  }
  if (shape.enum && !shape.enum.some(candidate => scalarEquals(candidate, value))) return {valid: false};
  const matchingType = types.find(type => valueMatchesType(value, type));
  if (!matchingType) return {valid: false};

  if (matchingType === 'string') {
    const text = value as string;
    if (shape.minLength !== undefined && text.length < shape.minLength ||
        shape.maxLength !== undefined && text.length > shape.maxLength) return {valid: false};
    return {valid: true, value: text};
  }
  if (matchingType === 'number' || matchingType === 'integer') {
    const number = value as number;
    if (shape.minimum !== undefined && number < shape.minimum ||
        shape.maximum !== undefined && number > shape.maximum) return {valid: false};
    return {valid: true, value: number};
  }
  if (matchingType === 'boolean' || matchingType === 'null') return {valid: true, value};
  if (matchingType === 'array') {
    const items = value as unknown[];
    if (shape.minItems !== undefined && items.length < shape.minItems ||
        shape.maxItems !== undefined && items.length > shape.maxItems || !shape.items) return {valid: false};
    const normalized: unknown[] = [];
    for (const item of items) {
      const parsed = normalizeShapeValue(shape.items, item, options);
      if (!parsed.valid) return {valid: false};
      normalized.push(parsed.value);
    }
    return {valid: true, value: normalized};
  }

  const object = value as Record<string, unknown>;
  const normalized: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  for (const required of shape.required ?? []) {
    if (!Object.hasOwn(object, required) || isBlankOptionalValue(object[required])) return {valid: false};
  }
  for (const [key, childValue] of Object.entries(object)) {
    if (!isSafeObjectKey(key)) return {valid: false};
    const childShape = shape.properties?.[key];
    if (childShape) {
      const parsed = normalizeShapeValue(childShape, childValue, options);
      if (!parsed.valid) return {valid: false};
      normalized[key] = parsed.value;
      continue;
    }
    if (shape.additionalProperties === undefined || shape.additionalProperties === false) return {valid: false};
    const parsed = normalizeShapeValue(shape.additionalProperties, childValue, options);
    if (!parsed.valid) return {valid: false};
    normalized[key] = parsed.value;
  }
  return {valid: true, value: normalized};
}

function buildFieldPathTree(paths: readonly string[]): FieldPathTree | null {
  const root: FieldPathTree = {terminal: false, children: new Map()};
  for (const path of paths) {
    let cursor = root;
    for (const segment of path.split('.')) {
      if (cursor.terminal) return null;
      const child = cursor.children.get(segment) ?? {terminal: false, children: new Map()};
      cursor.children.set(segment, child);
      cursor = child;
    }
    if (cursor.children.size > 0 || cursor.terminal) return null;
    cursor.terminal = true;
  }
  return root;
}

function containsOnlyDeclaredPaths(value: Record<string, unknown>, tree: FieldPathTree): boolean {
  for (const [key, childValue] of Object.entries(value)) {
    const child = tree.children.get(key);
    if (!child) return false;
    if (!child.terminal && (!isRecord(childValue) || !containsOnlyDeclaredPaths(childValue, child))) return false;
  }
  return true;
}

function readPath(value: Record<string, unknown>, path: string): {present: boolean; value?: unknown} {
  let cursor: unknown = value;
  for (const segment of path.split('.')) {
    if (!isRecord(cursor) || !Object.hasOwn(cursor, segment)) return {present: false};
    cursor = cursor[segment];
  }
  return {present: true, value: cursor};
}

function setPath(target: Record<string, unknown>, path: string, value: unknown): void {
  const segments = path.split('.');
  if (segments.some(segment => !isSafeObjectKey(segment))) return;
  let cursor = target;
  segments.forEach((segment, index) => {
    if (index === segments.length - 1) {
      cursor[segment] = value;
      return;
    }
    const next = Object.hasOwn(cursor, segment) && isRecord(cursor[segment])
      ? cursor[segment]
      : Object.create(null) as Record<string, unknown>;
    cursor[segment] = next;
    cursor = next;
  });
}

function hasAmbiguousFieldPaths(paths: readonly string[]): boolean {
  return buildFieldPathTree(paths) === null;
}

function readJsonTypes(value: unknown): ConnectNodeJsonType[] | null {
  if (typeof value === 'string') return JSON_TYPES.has(value as ConnectNodeJsonType)
    ? [value as ConnectNodeJsonType]
    : null;
  if (!Array.isArray(value) || value.length < 1 || value.length > 4) return null;
  const types = value.filter(candidate => typeof candidate === 'string') as string[];
  if (types.length !== value.length || new Set(types).size !== types.length ||
      types.some(type => !JSON_TYPES.has(type as ConnectNodeJsonType))) return null;
  return types as ConnectNodeJsonType[];
}

function readEnum(value: unknown, types: readonly ConnectNodeJsonType[]): ConnectNodeJsonScalar[] | null {
  if (value === undefined) return null;
  if (!Array.isArray(value) || value.length < 1 || value.length > MAX_ENUM_VALUES ||
      types.some(type => type === 'array' || type === 'object')) return null;
  const result: ConnectNodeJsonScalar[] = [];
  const seen = new Set<string>();
  for (const candidate of value) {
    if (!isJsonScalar(candidate) || !types.some(type => valueMatchesType(candidate, type)) ||
        typeof candidate === 'string' && candidate.length > MAX_ENUM_STRING_LENGTH) return null;
    const canonical = JSON.stringify(candidate);
    if (seen.has(canonical)) return null;
    seen.add(canonical);
    result.push(candidate);
  }
  return result;
}

function readUniqueFieldPaths(value: unknown, allowEmpty: boolean): string[] | null {
  if (!Array.isArray(value) || !allowEmpty && value.length < 1 || value.length > MAX_FIELDS) return null;
  const paths: string[] = [];
  for (const candidate of value) {
    if (typeof candidate !== 'string' || !isFieldPath(candidate) || paths.includes(candidate)) return null;
    paths.push(candidate);
  }
  return paths;
}

function readFieldPathGroups(value: unknown): string[][] | null {
  if (!Array.isArray(value) || value.length < 1 || value.length > MAX_FIELDS) return null;
  const groups: string[][] = [];
  const seen = new Set<string>();
  for (const candidate of value) {
    const group = readUniqueFieldPaths(candidate, false);
    if (!group) return null;
    const canonical = [...group].sort().join('\n');
    if (seen.has(canonical)) return null;
    seen.add(canonical);
    groups.push(group);
  }
  return groups;
}

function readUniqueTextList(value: unknown, maxLength: number): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const result: string[] = [];
  for (const candidate of value) {
    if (!isBoundedText(candidate, maxLength)) return undefined;
    const text = candidate.trim();
    if (result.includes(text)) return undefined;
    result.push(text);
  }
  return result;
}

function readFiniteNumber(
  value: Record<string, unknown>,
  key: string,
): {present: boolean; invalid: boolean; value?: number} {
  if (!Object.hasOwn(value, key)) return {present: false, invalid: false};
  const candidate = value[key];
  return typeof candidate === 'number' && Number.isFinite(candidate)
    ? {present: true, invalid: false, value: candidate}
    : {present: true, invalid: true};
}

function readNonNegativeInteger(
  value: Record<string, unknown>,
  key: string,
): {present: boolean; invalid: boolean; value?: number} {
  if (!Object.hasOwn(value, key)) return {present: false, invalid: false};
  const candidate = value[key];
  return typeof candidate === 'number' && Number.isInteger(candidate) && candidate >= 0
    ? {present: true, invalid: false, value: candidate}
    : {present: true, invalid: true};
}

function shapeTypes(shape: ConnectNodeFieldShape): ConnectNodeJsonType[] {
  if (typeof shape.type === 'string') return [shape.type];
  return Array.isArray(shape.type) ? shape.type : [];
}

function isScalarShape(shape: ConnectNodeFieldShape): boolean {
  if (shape.oneOf) return shape.oneOf.length > 0 && shape.oneOf.every(isScalarShape);
  const types = shapeTypes(shape);
  return types.length > 0 && types.every(type => type !== 'array' && type !== 'object');
}

function shapeAcceptsScalar(shape: ConnectNodeFieldShape, value: ConnectNodeJsonScalar): boolean {
  if (shape.oneOf) return shape.oneOf.some(candidate => shapeAcceptsScalar(candidate, value));
  if (!shapeTypes(shape).some(type => valueMatchesType(value, type))) return false;
  if (shape.enum && !shape.enum.some(candidate => scalarEquals(candidate, value))) return false;
  if (typeof value === 'string' && (
    shape.minLength !== undefined && value.length < shape.minLength ||
    shape.maxLength !== undefined && value.length > shape.maxLength
  )) return false;
  if (typeof value === 'number' && (
    shape.minimum !== undefined && value < shape.minimum ||
    shape.maximum !== undefined && value > shape.maximum
  )) return false;
  return true;
}

function typesOverlap(left: ConnectNodeJsonType, right: ConnectNodeJsonType): boolean {
  return left === right || left === 'number' && right === 'integer' ||
    left === 'integer' && right === 'number';
}

function outputTypeAssignableToTarget(
  output: ConnectNodeJsonType,
  target: ConnectNodeJsonType,
): boolean {
  return output === target || output === 'integer' && target === 'number';
}

function valueMatchesType(value: unknown, type: ConnectNodeJsonType): boolean {
  if (type === 'null') return value === null;
  if (type === 'array') return Array.isArray(value);
  if (type === 'object') return isRecord(value);
  if (type === 'integer') return typeof value === 'number' && Number.isInteger(value);
  if (type === 'number') return typeof value === 'number' && Number.isFinite(value);
  return typeof value === type;
}

function scalarEquals(left: ConnectNodeJsonScalar, right: unknown): boolean {
  return Object.is(left, right);
}

function isBlankOptionalValue(value: unknown): boolean {
  return value === undefined || typeof value === 'string' && value.trim().length === 0 ||
    Array.isArray(value) && value.length === 0 || isRecord(value) && Object.keys(value).length === 0;
}

function isFieldPath(value: string): boolean {
  return value.length <= MAX_FIELD_PATH_LENGTH && FIELD_PATH.test(value) &&
    value.split('.').every(isSafeObjectKey);
}

function isSafeObjectKey(value: string): boolean {
  return !RESERVED_OBJECT_KEYS.has(value.toLowerCase());
}

function isBoundedText(value: unknown, maxLength: number): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= maxLength;
}

function isJsonScalar(value: unknown): value is ConnectNodeJsonScalar {
  return value === null || typeof value === 'string' || typeof value === 'boolean' ||
    typeof value === 'number' && Number.isFinite(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) &&
    (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);
}

function isExactRecord(value: unknown, allowedKeys: readonly string[]): value is Record<string, unknown> {
  return isRecord(value) && Object.keys(value).every(key => allowedKeys.includes(key));
}

function cloneJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(cloneJson);
  if (isRecord(value)) return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, cloneJson(child)]));
  return value;
}
